import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { IonicModule, ModalController, Platform } from '@ionic/angular';
import { TranslateModule } from '@ngx-translate/core';
import { AnimationItem } from 'lottie-web';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import Swiper from 'swiper';
import { SolInputComponent } from '../common/components/forms/sol-input/sol-input.component';
import { INPUT_TYPE } from '../common/components/forms/sol-input/sol-input.enums';
import { ActivationService } from '../common/services/activation.service';

@Component({
  selector: 'sol-activation',
  templateUrl: './activation.component.html',
  styleUrl: './activation.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    ReactiveFormsModule,
    SolInputComponent,
    TranslateModule,
    LottieComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ActivationComponent implements AfterViewInit {
  @Input() data: { cardID: string } | undefined;
  @ViewChild('swiper', { static: false }) swiperElRef!: ElementRef<HTMLElement>;
  @ViewChild('videoEl', { static: true })
  videoEl!: ElementRef<HTMLVideoElement>;
  /** slide atual */
  currentSlide = 0;
  /** representa status de ativacao */
  activating: boolean = false;
  isPinValid: boolean | undefined;
  /** lottie animation */
  animationOptions: AnimationOptions = {
    path: '../../assets/images/ok.json',
  };
  passwordValidations = {
    length: false,
    specialChar: false,
    numbers: false,
    uppercase: false,
    lowercase: false,
    match: false,
  };
  TYPE_OF_INPUT = INPUT_TYPE;
  isMobile = false;
  private platform = inject(Platform);
  private activationService = inject(ActivationService);
  private modalCtrl = inject(ModalController);

  private matchEmails: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const group = control as FormGroup;
    const email = group.get('email')?.value;
    const confirmEmail = group.get('confirmEmail')?.value;
    return email === confirmEmail ? null : { emailsDoNotMatch: true };
  };

  activationForm = new FormGroup(
    {
      name: new FormControl(null, [Validators.required]),
      email: new FormControl(null, [Validators.email, Validators.required]),
      confirmEmail: new FormControl(null, [
        Validators.email,
        Validators.required,
      ]),
      pin: new FormControl(null, [
        Validators.required,
        Validators.pattern(/^\d{4}$/),
      ]),
      password: new FormControl(null, [
        Validators.required,
        Validators.minLength(6),
      ]),
      confirmPassword: new FormControl(null, [Validators.required]),
    },
    { validators: [this.matchEmails] }
  );

  constructor() {
    this.activationForm.get('password')?.valueChanges.subscribe(() => {
      this.validatePassword();
    });

    this.activationForm.get('confirmPassword')?.valueChanges.subscribe(() => {
      this.validatePassword();
    });
  }

  ngAfterViewInit(): void {
    const swiperRef = (this.swiperElRef.nativeElement as any).swiper as Swiper;
    swiperRef.allowTouchMove = false;
    const video = this.videoEl.nativeElement;
    if (video) {
      video.muted = true;
      video.play().catch((error) => console.warn('Autoplay prevented:', error));
    }
    const userAgent =
      navigator.userAgent || navigator.vendor || (window as any).opera;

    // Detecta iOS ou Android pelo userAgent
    this.isMobile = /android|iphone|ipad|ipod/i.test(userAgent);
    console.log(this.isMobile);
  }

  validatePassword() {
    const onConfirmStep = this.currentSlide === 6;
    // 🔹 Pega o valor da senha que está sendo digitada na tela atual
    const activePassword = onConfirmStep
      ? this.activationForm.get('confirmPassword')?.value || ''
      : this.activationForm.get('password')?.value || '';

    const comparePassword = this.activationForm.get('password')?.value || '';

    // Requisitos
    this.passwordValidations.length = activePassword.length >= 8;
    this.passwordValidations.specialChar = /[@&#*!\/]/.test(activePassword);
    this.passwordValidations.numbers =
      (activePassword.match(/\d/g) || []).length >= 3;
    this.passwordValidations.uppercase = /[A-Z]/.test(activePassword);
    this.passwordValidations.lowercase = /[a-z]/.test(activePassword);

    const senhaValida =
      this.passwordValidations.length &&
      this.passwordValidations.specialChar &&
      this.passwordValidations.numbers &&
      this.passwordValidations.uppercase &&
      this.passwordValidations.lowercase;

    // Match só na confirmação
    if (onConfirmStep && senhaValida) {
      this.passwordValidations.match = activePassword === comparePassword;
      this.activationForm
        .get('confirmPassword')
        ?.setErrors(this.passwordValidations.match ? null : { mismatch: true });
    } else {
      this.passwordValidations.match = false;
      this.activationForm.get('confirmPassword')?.setErrors(null);
    }
  }

  isFormValid(): boolean {
    const senhaValida =
      this.passwordValidations.length &&
      this.passwordValidations.specialChar &&
      this.passwordValidations.numbers &&
      this.passwordValidations.uppercase &&
      this.passwordValidations.lowercase;

    if (this.activationForm.get('password')) {
      return senhaValida;
    } else {
      return senhaValida && this.passwordValidations.match;
    }
  }

  animationCreated(animationItem: AnimationItem): void {
    console.log(animationItem);
  }

  async slideNavigation(forwardDirection = true, activate = false) {
    if (activate) {
      await this.onActivateCard();
      return;
    }
    if (this.currentSlide === 0) {
      this.activationService.expandModal.next(true);
    }
    const swiperEl = this.swiperElRef.nativeElement;
    const swiper = (swiperEl as any).swiper as Swiper;
    forwardDirection ? swiper.slideNext() : swiper.slidePrev();
    this.currentSlide = (swiperEl as any).swiper.activeIndex;

    // Se for o slide de confirmação
    if (this.currentSlide === 6) {
      // <-- depois trocamos pelo valor certo do log
      this.activationForm.get('confirmPassword')?.reset();
      this.passwordValidations = {
        length: false,
        specialChar: false,
        numbers: false,
        uppercase: false,
        lowercase: false,
        match: false,
      };
    }
  }

  async dismiss() {
    const m = await this.modalCtrl.getTop();
    await m?.dismiss();
  }

  private async onActivateCard() {
    this.activating = true;
    this.activationForm.disable();
    const data = this.activationForm.value as any;
    data.cardID = this.data?.cardID;
    /** objeto com o nome do usuario separado em primeiro e ultimo nome */
    const nameOfUsersObj = this.activationService.sortUserName(data.name);
    delete data.name;
    data.firstName = nameOfUsersObj.firstName;
    data.lastName = nameOfUsersObj.lastName;
    data.pin = Number(data.pin);
    const result = await this.activationService.activateCard(data);
    if (result.data.code === 'invalid-argument') {
      this.isPinValid = false;
      const swiperEl = this.swiperElRef.nativeElement;
      const swiper = (swiperEl as any).swiper as Swiper;
      swiper.slideTo(4);
      this.activationForm.enable();
    }
    if (result.data.status === 200) {
      this.slideNavigation();
    }
    this.activating = false;
  }
}
